import { useEffect, useMemo, type ReactElement } from "react";
import { useMatches } from "react-router";

import { useGetNodeTitleAndIsPlaceholder } from "../nodes/useGetNodeTitleAndIsPlaceholder.ts";
import { useContextPathNodeIds } from "./context.ts";
import { useGetNodeRoutePath } from "./useGetNodeRoutePath.ts";

export type Breadcrumb = {
  url: string;
  title: string | ReactElement<any>;
  id: string;
};

export type Handle = {
  crumb: string;
};

const useNodeDesignersBreadcrumbs = (): Breadcrumb[] => {
  const getTitleAndIsPlaceholder = useGetNodeTitleAndIsPlaceholder();
  const nodeIds = useContextPathNodeIds();
  const getNodeRoutePath = useGetNodeRoutePath();

  return useMemo(() => {
    if (nodeIds.length === 0) {
      return [];
    }

    return nodeIds
      .map((id): Breadcrumb | undefined => {
        const { title, isPlaceholder } = getTitleAndIsPlaceholder(id);
        if (isPlaceholder) return undefined;
        return {
          url: getNodeRoutePath(id),
          title,
          id: id.toString(),
        };
      })
      .filter((breadcrumb) => !!breadcrumb);
  }, [nodeIds, getTitleAndIsPlaceholder, getNodeRoutePath]);
};

const useRouterBreadcrumbs = (noNodeCrumbs: boolean): Breadcrumb[] => {
  const matches = useMatches();
  return useMemo(() => {
    const makeCrumbs = matches
      .map((match): ((isLast: boolean) => Breadcrumb) | null => {
        // Maybe there's a nicer way to validate route handles?
        if (
          match.handle &&
          typeof match.handle === "object" &&
          "getBreadcrumbTitle" in match.handle &&
          typeof match.handle.getBreadcrumbTitle === "function"
        ) {
          const { getBreadcrumbTitle } = match.handle;
          return (isLast: boolean) => {
            const title = getBreadcrumbTitle(match, isLast);
            return { url: match.pathname, title, id: match.id };
          };
        }
        return null;
      })
      .filter((makeCrumb) => !!makeCrumb);
    return makeCrumbs.map((makeCrumb, index) => makeCrumb(noNodeCrumbs && index === makeCrumbs.length - 1));
  }, [matches, noNodeCrumbs]);
};

export const useBreadcrumbs = () => {
  const nodeCrumbs = useNodeDesignersBreadcrumbs();
  const routerBreadcrumbs = useRouterBreadcrumbs(!nodeCrumbs.length);

  const crumbs = useMemo(() => [...routerBreadcrumbs, ...nodeCrumbs], [nodeCrumbs, routerBreadcrumbs]);

  // note: the module title has a hack to update the document title directly if it's the last breadcrumb
  // since it's rendered with a component.
  const lastCrumbTitle = useMemo(() => {
    const lastCrumb = crumbs[crumbs.length - 1];
    return lastCrumb && typeof lastCrumb.title === "string" ? lastCrumb.title : undefined;
  }, [crumbs]);

  useEffect(() => {
    if (lastCrumbTitle) {
      document.title = `${lastCrumbTitle} - Interact`;
    }
  }, [lastCrumbTitle]);

  return crumbs;
};
